import { useState, useCallback, useMemo, type ReactNode } from 'react';
import type { StoredDesign } from '@/services/storage';
import type { LibraryTab } from '@/features/library/components/LibraryTabs';
import type { CommunityFolder } from '@/types';
import { LibraryContext } from './libraryContext';
import { useSide } from './useSide';

export interface LibraryProviderProps {
  children: ReactNode;
}

export function LibraryProvider({ children }: LibraryProviderProps) {
  const [selectedDesign, setSelectedDesign] = useState<StoredDesign | null>(null);
  const [activeTab, setActiveTabState] = useState<LibraryTab>(() => {
    const stored = sessionStorage.getItem('library-active-tab');
    return stored === 'designs' ||
      stored === 'autosaves' ||
      stored === 'community' ||
      stored === 'projects'
      ? stored
      : 'designs';
  });

  const setActiveTab = useCallback((tab: LibraryTab) => {
    setActiveTabState(tab);
    sessionStorage.setItem('library-active-tab', tab);
  }, []);
  const { activeSide } = useSide();

  // Community state
  const [selectedFolder, setSelectedFolder] = useState<CommunityFolder | null>(null);
  const [viewingFolderDetail, setViewingFolderDetail] = useState(false);

  const selectDesign = useCallback((design: StoredDesign | null) => {
    setSelectedDesign(design);
  }, []);

  const selectFolder = useCallback((folder: CommunityFolder | null) => {
    setSelectedFolder(folder);
    // Auto-enter detail view when a folder is selected
    setViewingFolderDetail(folder !== null);
  }, []);

  // Compute the active side blob based on current side
  const activeSideBlob = useMemo(() => {
    if (!selectedDesign) return null;
    return activeSide === 'front' ? selectedDesign.frontBlob : selectedDesign.backBlob;
  }, [selectedDesign, activeSide]);

  const value = useMemo(
    () => ({
      selectedDesign,
      activeSideBlob,
      selectDesign,
      activeTab,
      setActiveTab,
      // Community
      selectedFolder,
      selectFolder,
      viewingFolderDetail,
      setViewingFolderDetail,
    }),
    [
      selectedDesign,
      activeSideBlob,
      selectDesign,
      activeTab,
      setActiveTab,
      selectedFolder,
      selectFolder,
      viewingFolderDetail,
    ]
  );

  return <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>;
}
